import React, { useState, useEffect } from 'react';
import { crmAPI, adminAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Users, Phone, Mail, Plus, Clock, AlertTriangle, Filter, Search } from 'lucide-react';

const LEAD_STAGES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'attempted_contact', label: 'Attempted Contact', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'in_conversation', label: 'In Conversation', color: 'bg-purple-100 text-purple-800' },
  { value: 'quoted', label: 'Quoted', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-800' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800' },
  { value: 'dormant', label: 'Dormant', color: 'bg-gray-100 text-gray-800' },
];

const LEAD_SOURCES = [
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone', label: 'Phone' },
  { value: 'web_form', label: 'Web Form' },
  { value: 'referral', label: 'Referral' },
  { value: 'repeat', label: 'Repeat Customer' },
];

const LEAD_CATEGORIES = [
  { value: 'awards', label: 'Awards' },
  { value: 'printing', label: 'Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'promo', label: 'Promo' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
  { value: 'med', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' },
];

export default function AdminLeads({ onViewLead, initialContactId = null }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ stage: '', source: '', overdue: false, contact_id: initialContactId });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [newLead, setNewLead] = useState({
    owner_user_id: '',
    stage: 'new',
    source: 'phone',
    next_followup_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    category: '',
    estimated_value: '',
    priority: 'med',
    notes: '',
    account_id: '',
  });

  useEffect(() => {
    fetchLeads();
    fetchUsers();
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.stage) params.stage = filter.stage;
      if (filter.source) params.source = filter.source;
      if (filter.overdue) params.overdue = true;
      if (filter.contact_id) params.contact_id = filter.contact_id;
      
      const data = await crmAPI.getLeads(params);
      setLeads(data.leads || []);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await adminAPI.getUsers({ limit: 100 });
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await crmAPI.getAccounts({ limit: 100 });
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const handleCreateLead = async () => {
    try {
      const leadData = {
        ...newLead,
        next_followup_at: new Date(newLead.next_followup_at).toISOString(),
        estimated_value: newLead.estimated_value ? parseFloat(newLead.estimated_value) : null,
      };
      
      await crmAPI.createLead(leadData);
      setShowCreateModal(false);
      setNewLead({
        owner_user_id: '',
        stage: 'new',
        source: 'phone',
        next_followup_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        category: '',
        estimated_value: '',
        priority: 'med',
        notes: '',
        account_id: '',
      });
      fetchLeads();
    } catch (err) {
      console.error('Failed to create lead:', err);
      alert('Failed to create lead: ' + err.message);
    }
  };

  const handleUpdateStage = async (leadId, newStage) => {
    try {
      await crmAPI.updateLead(leadId, { stage: newStage });
      fetchLeads();
    } catch (err) {
      console.error('Failed to update lead:', err);
    }
  };

  const getStageStyle = (stage) => {
    const found = LEAD_STAGES.find(s => s.value === stage);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const getStageName = (stage) => {
    const found = LEAD_STAGES.find(s => s.value === stage);
    return found ? found.label : stage;
  };

  const getPriorityStyle = (priority) => {
    const found = PRIORITIES.find(p => p.value === priority);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const isOverdue = (followupDate) => {
    return new Date(followupDate) < new Date();
  };

  return (
    <div className="p-6" data-testid="admin-leads-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads Pipeline</h1>
          <p className="text-gray-600">Manage and track sales leads</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-lead-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Lead
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          
          <Select value={filter.stage || "all"} onValueChange={(v) => setFilter({ ...filter, stage: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="stage-filter">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {LEAD_STAGES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filter.source || "all"} onValueChange={(v) => setFilter({ ...filter, source: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="source-filter">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {LEAD_SOURCES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            variant={filter.overdue ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter({ ...filter, overdue: !filter.overdue })}
            data-testid="overdue-filter"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            Overdue Only
          </Button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leads-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Lead ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Account</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Stage</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Source</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Category</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Value</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Priority</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Next Follow-up</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-500">Loading...</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-500">No leads found</td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="border-b hover:bg-gray-50" data-testid={`lead-row-${lead.id}`}>
                    <td className="py-3 px-4 font-mono text-sm">{lead.id}</td>
                    <td className="py-3 px-4">{lead.account_name || '-'}</td>
                    <td className="py-3 px-4">
                      <Select 
                        value={lead.stage} 
                        onValueChange={(v) => handleUpdateStage(lead.id, v)}
                      >
                        <SelectTrigger className="w-36 h-8">
                          <Badge className={getStageStyle(lead.stage)}>{getStageName(lead.stage)}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STAGES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4 capitalize">{lead.source?.replace('_', ' ')}</td>
                    <td className="py-3 px-4 capitalize">{lead.category?.replace('_', ' ') || '-'}</td>
                    <td className="py-3 px-4">
                      {lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={getPriorityStyle(lead.priority)} variant="secondary">
                        {lead.priority?.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className={`flex items-center gap-1 ${isOverdue(lead.next_followup_at) ? 'text-red-600' : ''}`}>
                        {isOverdue(lead.next_followup_at) && <AlertTriangle className="w-4 h-4" />}
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">
                          {new Date(lead.next_followup_at).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        data-testid={`view-lead-${lead.id}`}
                        onClick={() => onViewLead?.(lead.id)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Lead Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg" data-testid="create-lead-modal">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Owner *</Label>
              <Select 
                value={newLead.owner_user_id} 
                onValueChange={(v) => setNewLead({ ...newLead, owner_user_id: v })}
              >
                <SelectTrigger data-testid="lead-owner-select">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source *</Label>
                <Select 
                  value={newLead.source} 
                  onValueChange={(v) => setNewLead({ ...newLead, source: v })}
                >
                  <SelectTrigger data-testid="lead-source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select 
                  value={newLead.category} 
                  onValueChange={(v) => setNewLead({ ...newLead, category: v })}
                >
                  <SelectTrigger data-testid="lead-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Account</Label>
              <Select 
                value={newLead.account_id} 
                onValueChange={(v) => setNewLead({ ...newLead, account_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="lead-account-select">
                  <SelectValue placeholder="Select account (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Account</SelectItem>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Value</Label>
                <Input 
                  type="number" 
                  value={newLead.estimated_value}
                  onChange={(e) => setNewLead({ ...newLead, estimated_value: e.target.value })}
                  placeholder="$0.00"
                  data-testid="lead-value-input"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={newLead.priority} 
                  onValueChange={(v) => setNewLead({ ...newLead, priority: v })}
                >
                  <SelectTrigger data-testid="lead-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Next Follow-up *</Label>
              <Input 
                type="datetime-local" 
                value={newLead.next_followup_at}
                onChange={(e) => setNewLead({ ...newLead, next_followup_at: e.target.value })}
                data-testid="lead-followup-input"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                rows={3}
                placeholder="Add any notes about this lead..."
                data-testid="lead-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateLead}
              disabled={!newLead.owner_user_id || !newLead.next_followup_at}
              data-testid="submit-lead-btn"
            >
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
